import { useTranslation } from 'react-i18next';

import { send } from '@actual-app/core/platform/client/connection';
import type { ExchangeRateEntity } from '@actual-app/core/types/models';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import type { QueryClient } from '@tanstack/react-query';
import { v4 as uuidv4 } from 'uuid';

import { addNotification } from '#notifications/notificationsSlice';
import { useDispatch } from '#redux';
import type { AppDispatch } from '#redux/store';

import { exchangeRateQueries } from './queries';

const invalidateQueries = (queryClient: QueryClient) => {
  void queryClient.invalidateQueries({
    queryKey: exchangeRateQueries.lists(),
  });
};

const dispatchErrorNotification = (
  dispatch: AppDispatch,
  message: string,
  error?: Error,
) => {
  dispatch(
    addNotification({
      notification: {
        id: uuidv4(),
        type: 'error',
        message,
        pre: error ? error.message : undefined,
      },
    }),
  );
};

type CreateExchangeRatePayload = Omit<ExchangeRateEntity, 'id'>;

export function useCreateExchangeRateMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: CreateExchangeRatePayload) => {
      const result = await send('exchange-rates-create', payload);
      return result;
    },
    onSuccess: () => invalidateQueries(queryClient),
    onError: error => {
      console.error('Error creating exchange rate:', error);
      dispatchErrorNotification(
        dispatch,
        t('There was an error saving the exchange rate. Please try again.'),
        error,
      );
    },
  });
}

export function useDeleteExchangeRateMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async ({ id }: { id: ExchangeRateEntity['id'] }) => {
      return send('exchange-rates-delete', { id });
    },
    onSuccess: () => invalidateQueries(queryClient),
    onError: error => {
      console.error('Error deleting exchange rate:', error);
      dispatchErrorNotification(
        dispatch,
        t('There was an error deleting the exchange rate. Please try again.'),
        error,
      );
    },
  });
}

type FetchExchangeRatesPayload = {
  from_currency: string;
  to_currency: string;
  start_date: string;
  end_date: string;
};

export function useFetchExchangeRatesMutation() {
  const queryClient = useQueryClient();
  const dispatch = useDispatch();
  const { t } = useTranslation();

  return useMutation({
    mutationFn: async (payload: FetchExchangeRatesPayload) => {
      const result: { stored: number } = await send(
        'exchange-rates-fetch',
        payload,
      );
      return result;
    },
    onSuccess: () => invalidateQueries(queryClient),
    onError: error => {
      console.error('Error fetching exchange rates:', error);
      dispatchErrorNotification(
        dispatch,
        t(
          'There was an error fetching exchange rates. Please check your connection and try again.',
        ),
        error,
      );
    },
  });
}
